import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useSpring, useMotionValue } from 'framer-motion';
import aboutImage from '../assets/images/about.jpg';

// ─── Font injection ────────────────────────────────────────────────────────────
const injectLink = (href) => {
  if (!document.querySelector(`link[href="${href}"]`)) {
    const l = Object.assign(document.createElement('link'), { rel: 'stylesheet', href });
    document.head.appendChild(l);
  }
};
injectLink('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&display=swap');
injectLink('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  bg:      '#FAFAF7',
  bgWarm:  '#F2EDE4',
  bgCard:  '#FFFFFF',
  bgDark:  '#0E1525',
  text:    '#111827',
  soft:    '#6B7280',
  softer:  '#9CA3AF',
  accent:  '#FDDB1C',
  accentD: '#E8C900',
  border:  '#E8E2D8',
  borderL: '#F0EBE2',
  green:   '#10B981',
  blue:    '#2563EB',
  display: "'Lora', Georgia, serif",
  sans:    "'Bricolage Grotesque', system-ui, sans-serif",
};

// ─── Animated number counter ──────────────────────────────────────────────────
const useCounter = (target, duration = 1600) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const start = useCallback(() => setStarted(true), []);

  useEffect(() => {
    if (!started) return;
    const num = parseInt(target.replace(/\D/g, ''), 10);
    if (!num) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const p = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(eased * num));
      if (p < 1) requestAnimationFrame(step);
      else setCount(num);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return { display: count + target.replace(/[\d]/g, ''), start };
};

// ─── Counter card ─────────────────────────────────────────────────────────────
const CounterCard = ({ value, label, icon, accent, mob }) => {
  const ref = useRef(null);
  const { display, start } = useCounter(value);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !triggered) { setTriggered(true); start(); obs.disconnect(); }
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 20, scale: 0.94 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, type: 'spring', stiffness: 200 }}
      viewport={{ once: true }}
      whileHover={{ y: -6, boxShadow: `0 16px 40px ${accent}22` }}
      style={{ background: T.bgCard, borderRadius: '18px', padding: '28px 22px', border: `1.5px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: '10px', cursor: 'default', transition: 'border-color 0.28s' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = accent}
      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
      <div style={{ width: '38px', height: '38px', background: `${accent}18`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`fas ${icon}`} style={{ fontSize: '15px', color: accent }} />
      </div>
      <div style={{ fontFamily: T.sans, fontSize: mob ? '30px' : '36px', fontWeight: 800, color: T.text, lineHeight: 1, letterSpacing: '-1px' }}>{display}</div>
      <div style={{ fontFamily: T.sans, fontSize: '12px', color: T.soft }}>{label}</div>
    </motion.div>
  );
};

// ─── Magnetic button ──────────────────────────────────────────────────────────
const MagneticBtn = ({ href, children, btnStyle }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 300, damping: 20 });
  const sy = useSpring(y, { stiffness: 300, damping: 20 });

  const handleMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width  / 2) * 0.28);
    y.set((e.clientY - r.top  - r.height / 2) * 0.28);
  };
  const reset = () => { x.set(0); y.set(0); };

  return (
    <motion.a ref={ref} href={href}
      style={{ ...btnStyle, x: sx, y: sy, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', textDecoration: 'none', whiteSpace: 'nowrap' }}
      onMouseMove={handleMove} onMouseLeave={reset}>
      {children}
    </motion.a>
  );
};

// ─── Shimmer partner card ─────────────────────────────────────────────────────
const PartnerCard = ({ p, i, mob }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: i * 0.08 }} viewport={{ once: true }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        background: T.bgCard, borderRadius: '20px',
        padding: mob ? '22px 20px' : '28px 24px',
        border: `1.5px solid ${hovered ? T.accent : T.border}`,
        boxShadow: hovered ? '0 12px 32px rgba(253,219,28,0.14)' : '0 4px 12px rgba(0,0,0,0.04)',
        display: 'flex', alignItems: 'flex-start', gap: '16px',
        transform: hovered ? 'translateY(-5px)' : 'none',
        transition: 'all 0.28s cubic-bezier(0.2,0.9,0.4,1)',
        position: 'relative', overflow: 'hidden',
      }}>
      {/* Shimmer sweep */}
      <motion.div
        initial={{ x: '-120%' }} animate={{ x: hovered ? '240%' : '-120%' }}
        transition={{ duration: 0.55, ease: 'easeInOut' }}
        style={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg,transparent 35%,rgba(253,219,28,0.18) 50%,transparent 65%)', pointerEvents: 'none', zIndex: 0 }}
      />
      <div style={{ width: '46px', height: '46px', background: hovered ? T.accent : T.bgDark, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.28s', position: 'relative', zIndex: 1 }}>
        <i className={`fas ${p.icon}`} style={{ fontSize: '17px', color: hovered ? T.text : T.accent, transition: 'color 0.28s' }} />
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h3 style={{ fontFamily: T.sans, fontSize: mob ? '14px' : '15px', fontWeight: 700, color: T.text, margin: '0 0 5px' }}>{p.name}</h3>
        <p style={{ fontFamily: T.sans, fontSize: '12px', color: T.soft, lineHeight: 1.6, margin: 0 }}>{p.desc}</p>
      </div>
    </motion.div>
  );
};

// ─── Animated SectionIntro ────────────────────────────────────────────────────
const SectionIntro = ({ tag, title, subtitle, center = false, light = false }) => (
  <div style={{ textAlign: center ? 'center' : 'left', marginBottom: '48px' }}>
    {tag && (
      <motion.div initial={{ opacity: 0, x: center ? 0 : -12 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} viewport={{ once: true }}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: center ? 'center' : 'flex-start', marginBottom: '12px' }}>
        <motion.span initial={{ width: 0 }} whileInView={{ width: '22px' }} transition={{ duration: 0.4, delay: 0.1 }} viewport={{ once: true }}
          style={{ height: '2px', background: T.accent, borderRadius: '1px', display: 'block' }} />
        <span style={{ fontFamily: T.sans, fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: light ? 'rgba(255,255,255,0.45)' : T.soft }}>{tag}</span>
      </motion.div>
    )}
    <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} viewport={{ once: true }}
      style={{ fontFamily: T.display, fontSize: 'clamp(1.9rem, 4vw, 2.8rem)', fontWeight: 500, color: light ? '#fff' : T.text, margin: '0 0 12px', lineHeight: 1.15, letterSpacing: '-0.4px' }}>
      {title}
    </motion.h2>
    {subtitle && (
      <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.2 }} viewport={{ once: true }}
        style={{ fontFamily: T.sans, fontSize: '15px', color: light ? 'rgba(255,255,255,0.5)' : T.soft, lineHeight: 1.75, margin: 0, maxWidth: center ? '480px' : '460px', marginLeft: center ? 'auto' : 0, marginRight: center ? 'auto' : 0 }}>
        {subtitle}
      </motion.p>
    )}
  </div>
);

// ─── Parallax blob ─────────────────────────────────────────────────────────────
const ParallaxBlob = ({ color, size, top, left, right, bottom, opacity, strength = 0.03 }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const bx = useSpring(mx, { stiffness: 40, damping: 20 });
  const by = useSpring(my, { stiffness: 40, damping: 20 });

  useEffect(() => {
    const move = (e) => {
      mx.set((e.clientX - window.innerWidth  / 2) * strength);
      my.set((e.clientY - window.innerHeight / 2) * strength);
    };
    window.addEventListener('mousemove', move, { passive: true });
    return () => window.removeEventListener('mousemove', move);
  }, [strength]);

  return (
    <motion.div style={{ position: 'absolute', top, left, right, bottom, width: size, height: size, borderRadius: '50%', background: color, opacity, filter: 'blur(90px)', pointerEvents: 'none', x: bx, y: by }} />
  );
};

// ─── About ────────────────────────────────────────────────────────────────────
const About = () => {
  const [mob, setMob] = useState(window.innerWidth <= 768);
  const [sm,  setSm]  = useState(window.innerWidth <= 480);
  const [imgRevealed, setImgRevealed] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => { document.title = 'Saha Travels | About'; }, []);
  useEffect(() => {
    const r = () => { setMob(window.innerWidth <= 768); setSm(window.innerWidth <= 480); };
    window.addEventListener('resize', r); return () => window.removeEventListener('resize', r);
  }, []);

  // Trigger clip-path reveal
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setImgRevealed(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    if (imgRef.current) obs.observe(imgRef.current);
    return () => obs.disconnect();
  }, []);

  const maxW = '1280px';

  const stats = [
    { value: '2019', label: 'Founded',       icon: 'fa-calendar', accent: T.accent  },
    { value: '180+', label: 'Vehicles',      icon: 'fa-car',      accent: T.blue    },
    { value: '50k+', label: 'Happy Riders',  icon: 'fa-users',    accent: T.green   },
    { value: '50+',  label: 'Corp. Clients', icon: 'fa-building', accent: '#E63946' },
  ];

  const partners = [
    { icon: 'fa-building',    name: 'Shankar Associates',        desc: 'Corporate travel partner since 2020' },
    { icon: 'fa-chart-line',  name: 'Cclyyics Services Pvt Ltd', desc: 'Employee transportation solutions'   },
    { icon: 'fa-laptop-code', name: 'Alldigi Tech Ltd',          desc: 'Tech park commute services'          },
    { icon: 'fa-palette',     name: 'Lalit Kala Academy',        desc: 'Cultural event transportation'       },
    { icon: 'fa-bolt',        name: 'Excell Generators',         desc: 'Staff shuttle services'              },
    { icon: 'fa-plane',       name: 'Tripgain Travels',          desc: 'Travel partner & collaboration'      },
  ];

  const values = [
    { icon: 'fa-handshake',    title: 'Trust & Reliability', desc: 'Every driver is background-verified and professionally trained. We never compromise on safety.' },
    { icon: 'fa-clock',        title: 'Always On Time',      desc: "Punctuality is non-negotiable. Your time matters \u2014 we're there before you even look at your watch." },
    { icon: 'fa-heart',        title: 'Chennai at Heart',    desc: 'Born here, built for here. We know every road, shortcut, and neighbourhood in the city.' },
    { icon: 'fa-shield-halved',title: 'Safety First',        desc: 'Live tracking, SOS support, and 24/7 operations so every journey is as safe as it is smooth.' },
  ];



  return (
    <>
      {/* ══ HERO ══════════════════════════════════════════════════════════════ */}
      <section style={{ background: T.bg, paddingTop: '78px', position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${T.border}` }}>
        <ParallaxBlob color={T.accent} size={mob ? '320px' : '560px'} top="-60px"  right={mob ? '-100px' : '-60px'} opacity={0.09} strength={0.025} />
        <ParallaxBlob color={T.blue}   size="300px"                   bottom="-40px" left="-60px"                  opacity={0.06} strength={0.04}  />

        <div style={{ maxWidth: maxW, margin: '0 auto', padding: mob ? '52px 20px 56px' : '72px 60px 80px', position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: mob ? '1fr' : '1.2fr 0.8fr', gap: mob ? '40px' : '60px', alignItems: 'center' }}>
          <div>
            {/* Pill */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: '50px', padding: '7px 14px 7px 9px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: T.green, display: 'block', animation: 'pulse-green 2s infinite' }} />
              <span style={{ fontFamily: T.sans, fontSize: '12px', fontWeight: 600, color: T.text }}>Proudly Chennai · Since 2019</span>
            </motion.div>

            {/* Word-by-word animated title */}
            <div style={{ overflow: 'hidden', marginBottom: '18px' }}>
              <h1 style={{ fontFamily: T.display, fontSize: mob ? '38px' : 'clamp(2.8rem,5vw,4.4rem)', fontWeight: 600, lineHeight: 1.1, color: T.text, margin: 0, letterSpacing: '-1.5px' }}>
                {[
                  { text: 'We Are ', delay: 0.1 },
                  { text: 'More Than', delay: 0.2, italic: true },
                ].map(({ text, delay, italic }, i) => (
                  <motion.span key={i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
                    style={{ display: 'inline', fontStyle: italic ? 'italic' : 'normal', fontWeight: italic ? 400 : 600 }}>
                    {text}{' '}
                  </motion.span>
                ))}
                <br />
                <motion.span initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.32, ease: [0.22, 1, 0.36, 1] }}
                  style={{ position: 'relative', display: 'inline-block' }}>
                  a Cab Service
                  <motion.span initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.5, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
                    style={{ position: 'absolute', bottom: '4px', left: 0, width: '100%', height: '9px', background: T.accent, zIndex: -1, opacity: 0.45, borderRadius: '3px', transformOrigin: 'left', display: 'block' }} />
                </motion.span>
              </h1>
            </div>

            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.48 }}
              style={{ fontFamily: T.sans, fontSize: '15.5px', color: T.soft, lineHeight: 1.76, margin: '0 0 36px', maxWidth: '480px' }}>
              SAHA Travels is Chennai's premier corporate travel partner — built on trust, powered by professionalism, and driven by a genuine love for this city.
            </motion.p>

            {/* Stats row */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.6 }}
              style={{ display: 'flex', gap: mob ? '20px' : '36px', flexWrap: 'wrap', paddingTop: '28px', borderTop: `1px solid ${T.borderL}` }}>
              {[['2019','Founded'],['180+','Vehicles'],['50k+','Riders'],['4.9\u2605','Rating']].map(([v, l], i) => (
                <motion.div key={l} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.62 + i * 0.07 }}>
                  <div style={{ fontFamily: T.sans, fontSize: mob ? '21px' : '26px', fontWeight: 800, color: T.text, letterSpacing: '-0.5px', lineHeight: 1 }}>{v}</div>
                  <div style={{ fontFamily: T.sans, fontSize: '11px', color: T.soft, marginTop: '4px' }}>{l}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Hero image — clip-path reveal */}
          <motion.div ref={imgRef} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.15 }}
            style={{ position: 'relative' }}>
            {/* Orbiting dashed ring */}
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
              style={{ position: 'absolute', top: '50%', left: '50%', width: '107%', height: '107%', border: `1.5px dashed ${T.accent}`, borderRadius: '26px', opacity: 0.18, pointerEvents: 'none', translateX: '-50%', translateY: '-50%' }} />

            <motion.div
              animate={{ clipPath: imgRevealed ? 'inset(0% 0% 0% 0% round 22px)' : 'inset(0% 100% 0% 0% round 22px)' }}
              transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              style={{ position: 'relative', zIndex: 1 }}>
              <img src={aboutImage} alt="SAHA Travels Fleet" style={{ width: '100%', borderRadius: '22px', objectFit: 'cover', aspectRatio: mob ? '16/10' : '4/3', boxShadow: '0 24px 60px rgba(0,0,0,0.12)', border: `1.5px solid ${T.border}` }} />
            </motion.div>

            {/* Dark badge bottom-left */}
            <motion.div initial={{ opacity: 0, scale: 0.7, y: 14 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.5, delay: 1.1, type: 'spring', stiffness: 260 }}
              style={{ position: 'absolute', bottom: mob ? '12px' : '24px', left: mob ? '12px' : '-20px', zIndex: 2, background: T.bgDark, borderRadius: '14px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.22)' }}>
              <motion.i className="fas fa-star" animate={{ rotate: [0, 20, -20, 0] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }} style={{ color: T.accent, fontSize: '12px' }} />
              <span style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 700, color: '#fff' }}>4.9 Rated in Chennai</span>
            </motion.div>

            {/* Light badge top-right */}
            <motion.div initial={{ opacity: 0, scale: 0.7, y: -14 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.5, delay: 1.25, type: 'spring', stiffness: 260 }}
              style={{ position: 'absolute', top: mob ? '12px' : '20px', right: mob ? '12px' : '-18px', zIndex: 2, background: T.bgCard, borderRadius: '14px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', border: `1px solid ${T.border}` }}>
              <i className="fas fa-car" style={{ color: T.accentD, fontSize: '12px' }} />
              <span style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 700, color: T.text }}>180+ Vehicles</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══ MARQUEE ═══════════════════════════════════════════════════════════ */}
      <div style={{ background: T.accent, overflow: 'hidden', padding: '13px 0', borderTop: `1px solid ${T.accentD}`, borderBottom: `1px solid ${T.accentD}` }}>
        <div style={{ display: 'flex', animation: 'marquee 22s linear infinite', whiteSpace: 'nowrap' }}>
          {Array(3).fill(['✦ Founded 2019','✦ 180+ Vehicles','✦ Chennai Experts','✦ Zero Cancellations','✦ Corporate Specialists','✦ 50,000+ Riders','✦ Verified Drivers','✦ 24/7 Support']).flat().map((t, i) => (
            <span key={i} style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 700, color: T.text, padding: '0 28px', letterSpacing: '0.02em' }}>{t}</span>
          ))}
        </div>
      </div>

      {/* ══ OUR STORY ════════════════════════════════════════════════════════ */}
      <section style={{ background: T.bgWarm, padding: mob ? '68px 20px' : '96px 60px', position: 'relative', overflow: 'hidden' }}>
        <ParallaxBlob color={T.blue} size="400px" top="-80px" right="-100px" opacity={0.04} strength={0.02} />
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: mob ? '48px' : '80px', alignItems: 'center' }}>

            <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true }}>
              <SectionIntro tag="Our Story" title={<>Vanakkam,<br />We're <em style={{ fontStyle: 'italic', fontWeight: 400, color: T.blue }}>SAHA Travels</em></>} />

              {[
                'Founded in Chennai in 2019, SAHA Travels started with a single car and a single promise: that every passenger would feel valued, safe, and comfortable.',
                'Today, we operate a fleet of <b>180+ vehicles</b> serving corporate clients, families, and individuals across Tamil Nadu. Our drivers are Chennai locals who know every road.',
                'From solo airport drops to full-scale employee shuttle programs, SAHA brings the same warmth and professionalism to every single ride.',
              ].map((para, i) => (
                <motion.p key={i} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} viewport={{ once: true }}
                  style={{ fontFamily: T.sans, fontSize: '15px', color: T.soft, lineHeight: 1.8, marginBottom: '16px' }}
                  dangerouslySetInnerHTML={{ __html: para.replace('<b>', `<strong style="color:${T.text}">`).replace('</b>', '</strong>') }} />
              ))}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '8px' }}>
                {[
                  { icon: 'fa-map-marker-alt', text: 'Serving all of Chennai and Tamil Nadu' },
                  { icon: 'fa-user-shield',    text: 'Fully verified, trained professional drivers' },
                  { icon: 'fa-heart',          text: 'Proudly Made in Chennai, Always for You' },
                ].map(({ icon, text }, i) => (
                  <motion.div key={text} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: 0.3 + i * 0.09 }} viewport={{ once: true }}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <motion.div whileHover={{ rotate: [0, -10, 10, 0], scale: 1.15 }} transition={{ duration: 0.4 }}
                      style={{ width: '34px', height: '34px', background: T.accent, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={`fas ${icon}`} style={{ fontSize: '13px', color: T.text }} />
                    </motion.div>
                    <span style={{ fontFamily: T.sans, fontSize: '14px', color: T.text, fontWeight: 500 }}>{text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Counter cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {stats.map((s) => <CounterCard key={s.label} {...s} mob={mob} />)}
            </div>
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS ══════════════════════════════════════════════════════ */}
      <section style={{ background: T.bg, padding: mob ? '68px 20px' : '96px 60px', position: 'relative', overflow: 'hidden' }}>
        <ParallaxBlob color={T.accent} size="340px" top="-60px" right="-60px" opacity={0.05} strength={0.02} />
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <SectionIntro center tag="How It Works" title={<>Booking a Ride<br /><em style={{ fontStyle: 'italic', fontWeight: 400 }}>is Simple</em></>} subtitle="Three easy steps and your SAHA driver is on the way." />

          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : 'repeat(3,1fr)', gap: mob ? '16px' : '24px', position: 'relative' }}>
            {/* Connector line */}
            {!mob && (
              <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true }}
                style={{ position: 'absolute', top: '52px', left: 'calc(33.33% - 10px)', width: 'calc(66.66% - 20px)', height: '2px', background: `linear-gradient(to right, ${T.accent}, ${T.accentD}, ${T.accent})`, transformOrigin: 'left', zIndex: 0, opacity: 0.5 }} />
            )}

            {[
              { step: '01', icon: 'fa-phone',        title: 'Call or WhatsApp',  desc: "Reach us in seconds \u2014 call or message us on WhatsApp. We're available 24/7 with zero wait queues." },
              { step: '02', icon: 'fa-map-pin',       title: 'Share Your Route',  desc: "Tell us your pickup, drop, date and time. We'll match you with the perfect vehicle from our fleet." },
              { step: '03', icon: 'fa-circle-check',  title: 'Ride in Comfort',   desc: 'Your verified SAHA driver arrives on time. Sit back, relax, and enjoy a smooth Chennai ride.' },
            ].map(({ step, icon, title, desc }, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true }}
                whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(253,219,28,0.14)' }}
                style={{ background: T.bgCard, border: `1.5px solid ${T.border}`, borderRadius: '20px', padding: mob ? '24px 20px' : '32px 28px', position: 'relative', zIndex: 1, cursor: 'default', transition: 'border-color 0.25s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = T.accent}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>

                {/* Watermark step number */}
                <div style={{ position: 'absolute', top: '12px', right: '18px', fontFamily: T.display, fontSize: '64px', fontWeight: 700, color: T.bgWarm, lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>{step}</div>

                <motion.div whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }} transition={{ duration: 0.4 }}
                  style={{ width: '52px', height: '52px', background: T.bgDark, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <i className={`fas ${icon}`} style={{ fontSize: '20px', color: T.accent }} />
                </motion.div>

                <h3 style={{ fontFamily: T.sans, fontSize: '16px', fontWeight: 700, color: T.text, margin: '0 0 10px', lineHeight: 1.3 }}>{title}</h3>
                <p style={{ fontFamily: T.sans, fontSize: '13px', color: T.soft, lineHeight: 1.75, margin: 0 }}>{desc}</p>

                {/* Arrow bubble between cards */}
                {!mob && i < 2 && (
                  <motion.div initial={{ opacity: 0, x: -6 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.13 + 0.5 }} viewport={{ once: true }}
                    style={{ position: 'absolute', top: '50px', right: '-14px', width: '28px', height: '28px', background: T.accent, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, boxShadow: '0 4px 12px rgba(253,219,28,0.35)' }}>
                    <i className="fas fa-arrow-right" style={{ fontSize: '11px', color: T.text }} />
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          {/* CTA nudge */}
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} viewport={{ once: true }}
            style={{ textAlign: 'center', marginTop: '44px' }}>
            <a href="tel:+918637658382" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 700, background: T.accent, color: T.text, padding: '14px 32px', borderRadius: '50px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '9px', boxShadow: '0 4px 20px rgba(253,219,28,0.35)', transition: 'transform 0.18s, box-shadow 0.18s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(253,219,28,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(253,219,28,0.35)'; }}>
              <i className="fas fa-phone" style={{ fontSize: '12px' }} /> Book Your Ride Now
            </a>
          </motion.div>
        </div>
      </section>

      {/* ══ VALUES ════════════════════════════════════════════════════════════ */}
      <section style={{ background: T.bgWarm, padding: mob ? '68px 20px' : '96px 60px', position: 'relative', overflow: 'hidden' }}>
        <ParallaxBlob color={T.accent} size="380px" bottom="-80px" left="-60px" opacity={0.06} strength={0.02} />
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <SectionIntro center tag="Our Values" title={<>What Drives<br /><em style={{ fontStyle: 'italic', fontWeight: 400 }}>Everything We Do</em></>} subtitle="Four principles that guide every driver, every booking, and every ride." />
          <div style={{ display: 'grid', gridTemplateColumns: sm ? '1fr' : mob ? '1fr 1fr' : 'repeat(4,1fr)', gap: mob ? '12px' : '16px' }}>
            {values.map((v, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: i * 0.09 }} viewport={{ once: true }}
                whileHover={{ y: -6, boxShadow: `0 12px 32px rgba(253,219,28,0.16)` }}
                style={{ background: T.bgCard, border: `1.5px solid ${T.border}`, borderRadius: '16px', padding: mob ? '18px' : '24px', cursor: 'default', transition: 'border-color 0.25s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = T.accent}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                <motion.div whileHover={{ rotate: [0, -12, 12, 0], scale: 1.12 }} transition={{ duration: 0.4 }}
                  style={{ width: '42px', height: '42px', background: T.bgWarm, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px', border: `1px solid ${T.border}` }}>
                  <i className={`fas ${v.icon}`} style={{ fontSize: '16px', color: T.text }} />
                </motion.div>
                <h3 style={{ fontFamily: T.sans, fontSize: mob ? '13px' : '14px', fontWeight: 700, color: T.text, margin: '0 0 7px', lineHeight: 1.3 }}>{v.title}</h3>
                <p style={{ fontFamily: T.sans, fontSize: '12px', color: T.soft, lineHeight: 1.7, margin: 0 }}>{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CORPORATE PARTNERS ════════════════════════════════════════════════ */}
      <section style={{ background: T.bg, padding: mob ? '68px 20px' : '96px 60px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <SectionIntro tag="Trusted By" title={<>Our Corporate<br /><em style={{ fontStyle: 'italic', fontWeight: 400 }}>Partners</em></>} subtitle="Leading companies across Chennai trust SAHA for their transportation needs." />
          <div style={{ display: 'grid', gridTemplateColumns: sm ? '1fr' : mob ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: mob ? '14px' : '20px' }}>
            {partners.map((p, i) => <PartnerCard key={i} p={p} i={i} mob={mob} />)}
          </div>
        </div>
      </section>

      {/* ══ CTA ═══════════════════════════════════════════════════════════════ */}
      <section style={{ background: T.bgDark, padding: mob ? '68px 20px' : '96px 60px', position: 'relative', overflow: 'hidden' }}>
        <ParallaxBlob color={T.accent} size="380px" top="-80px"   right="-80px"  opacity={0.07} strength={0.025} />
        <ParallaxBlob color={T.blue}   size="300px" bottom="-60px" left="-60px"  opacity={0.08} strength={0.04}  />
        <div style={{ maxWidth: maxW, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr auto', gap: mob ? '36px' : '60px', alignItems: 'center' }}>
            <div>
              <SectionIntro light tag="Ready to Ride?" title={<>Let's Travel<br /><em style={{ fontStyle: 'italic', color: T.accent }}>Together.</em></>} subtitle="Whether it's a daily commute or a one-off trip — SAHA is ready 24/7 across Chennai." />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flexShrink: 0 }}>
              <MagneticBtn href="tel:+918637658382"
                btnStyle={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 700, background: T.accent, color: T.text, padding: '16px 32px', borderRadius: '50px', boxShadow: '0 4px 22px rgba(253,219,28,0.28)' }}>
                <i className="fas fa-phone" /> Call to Book
              </MagneticBtn>
              <MagneticBtn href="https://wa.me/918637658382?text=Hi%20Saha%20Travels%2C%20I%20want%20to%20book%20a%20ride"
                btnStyle={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 600, background: 'rgba(255,255,255,0.07)', color: '#fff', padding: '16px 32px', borderRadius: '50px', border: '1.5px solid rgba(255,255,255,0.14)' }}>
                <i className="fab fa-whatsapp" style={{ color: '#25D366', fontSize: '15px' }} /> WhatsApp Us
              </MagneticBtn>
            </div>
          </div>
        </div>
      </section>

      {/* ══ GLOBAL STYLES ════════════════════════════════════════════════════ */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { background: #FAFAF7; font-family: 'Bricolage Grotesque', system-ui, sans-serif; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
        img { max-width: 100%; display: block; }
        a { transition: opacity 0.18s; }
        ::selection { background: #FDDB1C; color: #111827; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #F2EDE4; }
        ::-webkit-scrollbar-thumb { background: #C9C0B0; border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: #FDDB1C; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-33.333%); } }
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 0 2px rgba(16,185,129,0.25); }
          50%       { box-shadow: 0 0 0 5px rgba(16,185,129,0.1); }
        }
      `}</style>
    </>
  );
};

export default About;